import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Unlock, ChevronLeft, ChevronRight } from 'lucide-react';
import { warehouseAPI } from '../../api/warehouses';

interface PeriodLockTableProps {
  warehouseId?: number;
  status?: 'locked' | 'unlocked';
  year?: number;
  onUnlockRequest: (lock: any) => void;
  refreshTrigger: number;
}

const PeriodLockTable: React.FC<PeriodLockTableProps> = ({
  warehouseId,
  status,
  year,
  onUnlockRequest,
  refreshTrigger,
}) => {
  const { t } = useTranslation();
  const [locks, setLocks] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadLocks();
  }, [warehouseId, status, year, page, refreshTrigger]);

  const loadLocks = async () => {
    setLoading(true);
    try {
      const result = await warehouseAPI.getPeriodLocks({
        warehouseId,
        status,
        year,
        page,
        pageSize,
      });

      setLocks(result.data);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (error) {
      console.error('Failed to load period locks:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">{t('common.loading')}</p>
      </div>
    );
  }

  if (locks.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <Lock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600">{t('warehouse.periodLock.noLocks')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                {t('warehouse.periodLock.warehouse')}
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                {t('warehouse.periodLock.period')}
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                {t('warehouse.periodLock.status')}
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                {t('warehouse.periodLock.lockedBy')}
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                {t('warehouse.periodLock.lockedAt')}
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                {t('warehouse.periodLock.lockReason')}
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                {t('warehouse.periodLock.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {locks.map((lock) => (
              <tr key={lock.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm">
                  <div>
                    <div className="font-medium text-gray-900">{lock.warehouseCode}</div>
                    <div className="text-gray-500 text-xs">{lock.warehouseName}</div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {lock.periodLabel}
                </td>
                <td className="px-4 py-3 text-sm">
                  {lock.status === 'locked' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                      <Lock className="w-3 h-3" />
                      {t('warehouse.periodLock.locked')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                      <Unlock className="w-3 h-3" />
                      {t('warehouse.periodLock.unlocked')}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {lock.lockedByName}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {formatDate(lock.lockedAt)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  <div className="max-w-xs truncate" title={lock.lockReason}>
                    {lock.lockReason}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">
                  {lock.status === 'locked' ? (
                    <button
                      onClick={() => onUnlockRequest(lock)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {t('warehouse.periodLock.unlock')}
                    </button>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {t('common.pagination.showing', { from: (page - 1) * pageSize + 1, to: Math.min(page * pageSize, total), total })}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm text-gray-600">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodLockTable;
